import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import {
  addDays,
  endOfMonth,
  isSameMonth,
  monthGridDays,
  sameDay,
  startOfMonth,
  startOfWeek,
  toDateKey,
} from "../../../utils/clientAppUtils";
import { sessionsOnDay, sessionKindsOnDay, isClubEventSession } from "../../../utils/clientScheduleUtils";
import { clubEventTag } from "../../../utils/competitionUtils";
import {
  getSessionDisplayStatus,
  sessionAccentStyle,
  groupName,
  pollBySessionKey,
  pollForSession,
  sessionDurationMinutes,
  trainerName,
} from "../../../utils/clientSessionUtils";
import { useClientRefresh } from "../../../hooks/useClientRefresh";
import CompetitionApplySheet from "./CompetitionApplySheet";
import SessionDetailSheet from "./SessionDetailSheet";
import SessionStatusBadge from "./SessionStatusBadge";
import s from "./clientApp.module.css";
import { Skeleton, useToast } from "../../ui";

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

function parseDateParam(raw) {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const d = new Date(`${raw}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function CalDayMarkers({ kinds, active }) {
  if (!kinds.training && !kinds.competition) return null;
  return (
    <span className={s.calDayMarkers} aria-hidden>
      {kinds.training > 0 ? (
        <i className={`${s.calDayDot} ${s.calDayDotTraining} ${active ? s.calDayDotOn : ""}`} />
      ) : null}
      {kinds.competition > 0 ? (
        <i className={`${s.calDayDot} ${s.calDayDotCompetition} ${active ? s.calDayDotOn : ""}`} />
      ) : null}
    </span>
  );
}

function CalLegend() {
  return (
    <div className={s.calLegend} aria-label="Обозначения в календаре">
      <span className={s.calLegendItem}>
        <i className={`${s.calLegendDot} ${s.calLegendDotTraining}`} aria-hidden />
        Тренировка
      </span>
      <span className={s.calLegendItem}>
        <i className={`${s.calLegendDot} ${s.calLegendDotCompetition}`} aria-hidden />
        Событие
      </span>
    </div>
  );
}

function CalEventCard({ session, groups, pollRow, onOpen }) {
  const status = getSessionDisplayStatus(session, pollRow);
  const duration = sessionDurationMinutes(session.start_datetime, session.end_datetime);
  const isCompetition = isClubEventSession(session);
  const isEvent = isCompetition;
  const eventTag = clubEventTag(session);
  const toneClass =
    s[`calEventTone${status.tone.charAt(0).toUpperCase()}${status.tone.slice(1)}`] ||
    s.calEventToneNeutral;
  const accentStyle = isEvent
    ? { "--cal-accent": "#d97706" }
    : sessionAccentStyle(session);

  return (
    <button
      type="button"
      className={`${s.calEvent} ${toneClass} ${isEvent ? s.calEventCompetition : ""}`}
      onClick={() => onOpen(session)}
      style={accentStyle}
    >
      <span className={s.calEventAccent} aria-hidden />
      <div className={s.calEventMain}>
        <div className={s.calEventHead}>
          <div className={s.calEventTimeBlock}>
            <span className={s.calEventTime}>{formatTimeRu(session.start_datetime)}</span>
            {session.end_datetime ? (
              <span className={s.calEventTimeEnd}>
                до {formatTimeRu(session.end_datetime)}
              </span>
            ) : null}
          </div>
          <SessionStatusBadge appointment={session} pollRow={pollRow} />
        </div>

        <span
          className={
            isEvent ? s.calEventTypeCompetition : s.calEventTypeTraining
          }
        >
          {isEvent ? eventTag || "Событие" : "Тренировка"}
        </span>

        <h3 className={s.calEventTitle}>{groupName(session, groups)}</h3>

        <div className={s.calEventMeta}>
          {!isEvent ? (
            <span className={s.calEventMetaRow}>
              <svg className={s.calEventMetaIcon} viewBox="0 0 24 24" aria-hidden>
                <path
                  d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                />
                <path
                  d="M4 20c0-3.3 2.7-6 6-6h4c3.3 0 6 2.7 6 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
              {trainerName(session)}
            </span>
          ) : (
            <span className={s.calEventMetaRow}>{eventTag || "Событие"}</span>
          )}
          {session.branch?.name ? (
            <span className={s.calEventMetaRow}>
              <svg className={s.calEventMetaIcon} viewBox="0 0 24 24" aria-hidden>
                <path
                  d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                />
                <circle cx="12" cy="10" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.75" />
              </svg>
              {session.branch.name}
            </span>
          ) : null}
          {duration ? (
            <span className={s.calEventMetaRow}>
              <svg className={s.calEventMetaIcon} viewBox="0 0 24 24" aria-hidden>
                <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.75" />
                <path
                  d="M12 7v5l3 2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
              {duration} мин
            </span>
          ) : null}
        </div>
      </div>
      <span className={s.calEventChevron} aria-hidden>
        ›
      </span>
    </button>
  );
}

export default function ClientCalendar() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { athleteQueryParam, activeAthleteId } = useClientAthlete();
  const [viewMode, setViewMode] = useState("day");

  const anchor = useMemo(() => {
    return parseDateParam(searchParams.get("date")) || new Date();
  }, [searchParams]);

  const [schedule, setSchedule] = useState([]);
  const [polls, setPolls] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [applyCompetition, setApplyCompetition] = useState(null);
  const [applying, setApplying] = useState(false);

  const selectDay = useCallback(
    (d) => {
      const next = new URLSearchParams(searchParams);
      next.set("date", toDateKey(d));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const monthStart = useMemo(() => startOfMonth(anchor), [anchor]);
  const monthEnd = useMemo(() => endOfMonth(anchor), [anchor]);

  const rangeFrom = useMemo(() => {
    if (viewMode === "month") return toDateKey(addDays(monthStart, -7));
    return toDateKey(addDays(weekStart, -7));
  }, [viewMode, monthStart, weekStart]);

  const rangeTo = useMemo(() => {
    if (viewMode === "month") return toDateKey(addDays(monthEnd, 14));
    return toDateKey(addDays(weekStart, 21));
  }, [viewMode, monthEnd, weekStart]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = { ...athleteQueryParam, from: rangeFrom, to: rangeTo };
      const [scheduleData, pollData, dash] = await Promise.all([
        clinicAPI.getMySchedule(q),
        clinicAPI.getMyTrainingPolls(athleteQueryParam).catch(() => ({ polls: [] })),
        clinicAPI.getMyClientDashboard(athleteQueryParam).catch(() => ({ groups: [] })),
      ]);
      setSchedule(scheduleData.sessions || []);
      setPolls(pollData.polls || []);
      setGroups(dash.groups || []);
      setDetail((prev) => {
        if (!prev) return null;
        const sid = prev.session_id || prev.id;
        return (
          (scheduleData.sessions || []).find(
            (row) => (row.session_id || row.id) === sid,
          ) || prev
        );
      });
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
      setSchedule([]);
    } finally {
      setLoading(false);
    }
  }, [rangeFrom, rangeTo, toast, athleteQueryParam]);

  useEffect(() => {
    load();
  }, [load, activeAthleteId]);

  useClientRefresh(load);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const monthDays = useMemo(() => monthGridDays(anchor), [anchor]);
  const pollMap = useMemo(() => pollBySessionKey(polls), [polls]);

  const daySessions = useMemo(
    () => sessionsOnDay(schedule, anchor),
    [schedule, anchor],
  );

  const weekLabel = `${formatDateRu(weekStart, { day: "numeric", month: "short" })} — ${formatDateRu(addDays(weekStart, 6), { day: "numeric", month: "short" })}`;
  const monthLabel = formatDateRu(monthStart, { month: "long", year: "numeric" });
  const detailPoll = pollForSession(pollMap, detail);
  const isToday = sameDay(anchor, new Date());

  const selectDayToday = () => selectDay(new Date());

  const shiftPeriod = (delta) => {
    if (viewMode === "month") {
      const d = new Date(anchor);
      d.setMonth(d.getMonth() + delta);
      selectDay(d);
      return;
    }
    selectDay(addDays(anchor, delta * 7));
  };

  return (
    <div className={s.calPage}>
      <header className={s.calHead}>
        <div className={s.calHeadTop}>
          <h1 className={s.calHeadTitle}>Календарь</h1>
          <div className={s.calViewToggle} role="tablist" aria-label="Режим календаря">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "day"}
              className={viewMode === "day" ? s.calViewBtnOn : s.calViewBtn}
              onClick={() => setViewMode("day")}
            >
              День
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "month"}
              className={viewMode === "month" ? s.calViewBtnOn : s.calViewBtn}
              onClick={() => setViewMode("month")}
            >
              Месяц
            </button>
          </div>
        </div>
        <div className={s.calHeadNav}>
          <button
            type="button"
            className={s.calNavBtn}
            onClick={() => shiftPeriod(-1)}
            aria-label={viewMode === "month" ? "Предыдущий месяц" : "Предыдущая неделя"}
          >
            ‹
          </button>
          <button
            type="button"
            className={s.calHeadRangeBtn}
            onClick={selectDayToday}
          >
            {viewMode === "month" ? monthLabel : weekLabel}
          </button>
          <button
            type="button"
            className={s.calNavBtn}
            onClick={() => shiftPeriod(1)}
            aria-label={viewMode === "month" ? "Следующий месяц" : "Следующая неделя"}
          >
            ›
          </button>
        </div>
      </header>

      {viewMode === "day" ? (
        <>
        <div className={s.calDayStrip}>
          {weekDays.map((d) => {
            const key = toDateKey(d);
            const active = sameDay(d, anchor);
            const kinds = sessionKindsOnDay(schedule, d);
            const dayToday = sameDay(d, new Date());
            return (
              <button
                key={key}
                type="button"
                className={`${s.calDay} ${active ? s.calDayOn : ""} ${dayToday ? s.calDayToday : ""}`}
                onClick={() => selectDay(d)}
              >
                <span>{d.toLocaleDateString("ru-RU", { weekday: "narrow" })}</span>
                <strong>{d.getDate()}</strong>
                <CalDayMarkers kinds={kinds} active={active} />
              </button>
            );
          })}
        </div>
        <CalLegend />
        </>
      ) : (
        <>
        <div className={s.calMonthGrid}>
          <div className={s.calMonthWeekdays}>
            {WEEKDAYS.map((wd) => (
              <span key={wd} className={s.calMonthWeekday}>
                {wd}
              </span>
            ))}
          </div>
          <div className={s.calMonthCells}>
            {monthDays.map((d) => {
              const key = toDateKey(d);
              const active = sameDay(d, anchor);
              const inMonth = isSameMonth(d, anchor);
              const kinds = sessionKindsOnDay(schedule, d);
              const dayToday = sameDay(d, new Date());
              return (
                <button
                  key={key}
                  type="button"
                  className={`${s.calMonthCell} ${active ? s.calMonthCellOn : ""} ${!inMonth ? s.calMonthCellMuted : ""} ${dayToday ? s.calMonthCellToday : ""}`}
                  onClick={() => selectDay(d)}
                >
                  <span className={s.calMonthCellNum}>{d.getDate()}</span>
                  {kinds.training > 0 || kinds.competition > 0 ? (
                    <span className={s.calMonthCellDots}>
                      {kinds.training > 0 ? (
                        <i
                          className={`${s.calMonthCellDot} ${s.calMonthCellDotTraining} ${active ? s.calMonthCellDotOn : ""}`}
                        />
                      ) : null}
                      {kinds.competition > 0 ? (
                        <i
                          className={`${s.calMonthCellDot} ${s.calMonthCellDotCompetition} ${active ? s.calMonthCellDotOn : ""}`}
                        />
                      ) : null}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
        <CalLegend />
        </>
      )}

      <div className={s.calBody}>
        <div className={s.calDayHeader}>
          <p className={s.calDayTitle}>
            {formatDateRu(anchor, { weekday: "long", day: "numeric", month: "long" })}
          </p>
          {isToday ? <span className={s.calDayBadge}>Сегодня</span> : null}
          {!loading && daySessions.length > 0 ? (
            <span className={s.calDayCount}>
              {daySessions.length}{" "}
              {daySessions.length === 1 ? "событие" : "события"}
            </span>
          ) : null}
        </div>

        {loading ? (
          <div className={s.calList}>
            <Skeleton height={120} style={{ borderRadius: 16 }} />
            <Skeleton height={120} style={{ borderRadius: 16, marginTop: 10 }} />
          </div>
        ) : daySessions.length === 0 ? (
          <div className={s.calEmpty}>
            <div className={s.calEmptyIcon} aria-hidden>
              <svg viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="17" rx="3" fill="none" stroke="currentColor" strokeWidth="1.75" />
                <path d="M3 9h18M8 2v4M16 2v4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
              </svg>
            </div>
            <p>Нет событий</p>
            <span>
              {viewMode === "month"
                ? "Выберите другой день в сетке или переключитесь на режим «День»"
                : "Расписание и соревнования появятся здесь после записи тренером"}
            </span>
          </div>
        ) : (
          <div className={s.calList}>
            {daySessions.map((a) => (
              <CalEventCard
                key={`${a.source || "session"}:${a.id}`}
                session={a}
                groups={groups}
                pollRow={pollForSession(pollMap, a)}
                onOpen={setDetail}
              />
            ))}
          </div>
        )}
      </div>

      {detail ? (
        <SessionDetailSheet
          appointment={detail}
          pollRow={detailPoll}
          groups={groups}
          onClose={() => setDetail(null)}
          onPollDone={load}
          onCompetitionApply={(comp) => {
            setDetail(null);
            setApplyCompetition(comp);
          }}
        />
      ) : null}

      {applyCompetition ? (
        <CompetitionApplySheet
          competition={applyCompetition}
          onClose={() => setApplyCompetition(null)}
          submitting={applying}
          onSubmit={async (data) => {
            setApplying(true);
            try {
              await clinicAPI.applyToCompetition(applyCompetition.id, data);
              toast.success("Заявка отправлена");
              setApplyCompetition(null);
              load();
            } catch (e) {
              toast.error(e.response?.data?.error || "Ошибка");
            } finally {
              setApplying(false);
            }
          }}
        />
      ) : null}
    </div>
  );
}
